import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { designSystem } from '@yola/ws-ui';
import { i18next, view, cssGenerator } from '@yola/ws-sdk';
import OptionSection from 'src/js/modules/common/components/option-section';
import elementSettings from 'src/js/modules/element-settings';
import prepareInstructionsForColumnWidthReset from '../helpers/prepare-instructions-for-column-width-reset';
import previewGifs from '../constants/preview-gifs';
import attributes from '../constants/attributes';
import SwitchControl from './switch-control';

const { TabSelector, Box, HorizontalStack } = designSystem;
const {
  Context: ElementSettingsContext,
  helpers: { createBatch },
} = elementSettings;
const { COLUMN_WIDTH } = cssGenerator.constants.attributes;

const COLUMNS_AMOUNT_ID = 'columns-amount';

const tabs = [
  { id: '1-columns', label: '1' },
  { id: '2-columns', label: '2' },
  { id: '3-columns', label: '3' },
  { id: '4-columns', label: '4' },
  { id: '5-columns', label: '5', disabled: true },
  { id: '6-columns', label: '6' },
];

function ColumnsAmountControl({ elementId, onChange, noWrapControl, onNoWrapChange }) {
  const { state } = useContext(ElementSettingsContext);

  const columns = view.accessors.getLiveElement(elementId);
  const customWidthColumns = columns.querySelectorAll(`ws-column[${COLUMN_WIDTH}]`);

  const currentValue =
    get(state, `${elementId}.${attributes.LAYOUT}`) ||
    view.accessors.getElementAttribute(elementId, attributes.LAYOUT) ||
    '';
  const selected = !customWidthColumns.length ? currentValue : '';

  const handleChange = useCallback(
    (value) => {
      const batch = createBatch({
        elementIds: [elementId],
        attributes: [attributes.LAYOUT],
        value,
      });

      // If the columns have custom width, we need to remove it when changing the number of columns
      customWidthColumns.forEach((column) => {
        const columnId = view.accessors.getLiveElementId(column);
        batch[columnId] = {
          [COLUMN_WIDTH]: false,
        };
      });

      const instructions = prepareInstructionsForColumnWidthReset(elementId);

      onChange({
        batch: {
          ...batch,
          ...instructions,
        },
        controlId: COLUMNS_AMOUNT_ID,
      });
    },
    [customWidthColumns, elementId, onChange]
  );

  return (
    <Box
      paddingTop="spacing-xs"
      paddingLeft="spacing-m"
      paddingRight="spacing-m"
      paddingBottom="spacing-xs"
    >
      <OptionSection
        title={i18next.t('Columns per row')}
        wrap
        bold
        popoverProps={{
          content: i18next.t('Select the number of columns per row'),
          imgSrc: previewGifs.COLUMNS_AMOUNT_GIF,
        }}
      >
        <Box marginTop="spacing-2-xs">
          <HorizontalStack verticalAlign="center" align="space-between" gap="spacing-3-xs">
            <TabSelector size="small" activeTabId={selected} tabs={tabs} onClick={handleChange} />
            <SwitchControl {...noWrapControl} inverted onChange={onNoWrapChange} />
          </HorizontalStack>
        </Box>
      </OptionSection>
    </Box>
  );
}

ColumnsAmountControl.propTypes = {
  elementId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  noWrapControl: PropTypes.shape({
    id: PropTypes.string.isRequired,
    elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    attributes: PropTypes.arrayOf(PropTypes.string).isRequired,
    glyph: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  onNoWrapChange: PropTypes.func.isRequired,
};

export default ColumnsAmountControl;
